// TODO: Later replace with @ui-toolkit/text-slider
import { ReactNode, useEffect, useRef, useState } from 'react'
import {
  SxProps,
  Typography,
  TypographyProps,
  keyframes,
  styled,
} from '@mui/material'

const Container = styled('div')`
  width: 100%;
  position: relative;
  display: flex;
  height: 100%;
`

const StyledTypography = styled(Typography)`
  position: relative;
  max-width: 100%;
  display: flex;
  align-items: center;
  width: 100%;
  height: 20px;
  flex-shrink: 0;
`

const ScrollingText = styled('span')(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(0.5),
  color: 'inherit',
  fontSize: 'inherit',
  fontWeight: 'inherit',
  display: 'flex',
  alignItems: 'center',
}))

const scrollAnimation = keyframes`   
from {
  right: 10%;
  transform: translate(0, 0);
}
to {
  right: 0%;
  transform: translate(100%, 0);
}
`

interface Props {
  text?: string
  children?: ReactNode
  textVariant?: TypographyProps['variant']
  textColor?: TypographyProps['color']
  typographySx?: SxProps
  className?: string
  sx?: SxProps
}

export function TextSlider({
  text,
  children,
  textVariant,
  textColor,
  className,
  sx,
  typographySx,
}: Props) {
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false)
  const spanRef = useRef<HTMLSpanElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        const { current: spanEl } = spanRef
        const { current: containerEl } = containerRef

        if (!spanEl || !containerEl) return
        setIsOverflowing(containerEl.clientWidth < spanEl.scrollWidth)
      })
    })

    if (containerRef.current) observer.observe(containerRef.current)
    return () => {
      if (containerRef.current) observer.unobserve(containerRef.current)
    }
  }, [text])

  return (
    <Container ref={containerRef} className={className} sx={sx}>
      <StyledTypography
        noWrap
        variant={textVariant ?? 'subtitle3'}
        color={textColor ?? 'neutral900'}
        sx={typographySx}
      >
        <ScrollingText
          ref={spanRef}
          sx={{
            animation: isOverflowing
              ? `${scrollAnimation} 20s infinite linear`
              : null,
          }}
        >
          {children || text}
        </ScrollingText>
      </StyledTypography>
    </Container>
  )
}
